import { useTranslation } from 'react-i18next';
import { Icon } from '../../../shared/components/Icon';
import { useVoiceCommand } from '../context/VoiceCommandProvider';

export function VoiceCommandLauncher() {
  const { t } = useTranslation();
  const { view, models, toggleListening, install, packageBytes, confirm, cancel, dismiss } = useVoiceCommand();
  const busy = ['initializing', 'transcribing', 'understanding', 'preparing', 'executing'].includes(view.status);
  const visible = view.installPrompt || !['disabled', 'idle'].includes(view.status);

  return (
    <>
      <button
        className={`voice-launcher${view.status === 'listening' ? ' voice-launcher--listening' : ''}`}
        type="button"
        onClick={() => void toggleListening()}
        aria-label={view.status === 'listening' ? t('voice.stopListening') : t('voice.startListening')}
        title={t('voice.localPrivacy')}
        disabled={busy}
      >
        <Icon name={view.status === 'listening' ? 'stop' : 'microphone'} />
        <span className="sr-only">{t('voice.startListening')}</span>
      </button>

      {visible && (
        <section className="voice-surface" role="status" aria-live="polite" aria-label={t('voice.title')}>
          <header>
            <span><Icon name="microphone" /><strong>{t('voice.title')}</strong></span>
            <button className="icon-button" type="button" onClick={dismiss} aria-label={t('common.close')}><Icon name="close" /></button>
          </header>

          {view.installPrompt && !models.basicInstalled ? (
            <div className="voice-install">
              <strong>{t('voice.installTitle')}</strong>
              <p>{t('voice.installHint')}</p>
              <small>{formatBytes(packageBytes('basic'))}</small>
              {view.installProgress !== undefined && <progress max={1} value={view.installProgress} />}
              <button className="button button--primary" type="button" onClick={() => void install('basic')} disabled={view.installProgress !== undefined}>{t('voice.installBasic')}</button>
            </div>
          ) : (
            <div className="voice-status">
              {view.status === 'listening' && <div className="voice-level" aria-hidden="true"><i style={{ transform: `scaleX(${Math.max(0.04, view.audioLevel)})` }} /></div>}
              <strong>{t(`voice.state.${view.status}`)}</strong>
              {view.transcript && <q dir="auto">{view.transcript}</q>}
              {view.error && <p className="voice-error">{view.error}</p>}
              {view.prepared?.confirmation.required && (
                <div className="voice-confirmation">
                  <p>{view.prepared.confirmation.description ?? t('voice.confirmHint')}</p>
                  <dl>{Object.entries(view.prepared.resolvedParameters).map(([key, value]) => <div key={key}><dt>{key}</dt><dd>{String(value)}</dd></div>)}</dl>
                  <div><button className="button button--primary" type="button" onClick={() => void confirm()}>{t('voice.confirm')}</button><button className="button" type="button" onClick={() => void cancel()}>{t('common.cancel')}</button></div>
                </div>
              )}
              {view.prepared?.candidates && view.prepared.candidates.length > 0 && (
                <ul>{view.prepared.candidates.map((candidate) => <li key={candidate.id}>{candidate.displayName}{candidate.secondaryText ? ` — ${candidate.secondaryText}` : ''}</li>)}</ul>
              )}
              {view.status === 'listening' && <button className="button" type="button" onClick={() => void toggleListening()}>{t('voice.stopListening')}</button>}
            </div>
          )}
        </section>
      )}
    </>
  );
}

function formatBytes(bytes: number): string {
  if (bytes < 1024 ** 2) return `${Math.ceil(bytes / 1024)} KB`;
  return `${(bytes / 1024 ** 2).toFixed(0)} MB`;
}
